// Block J (UI-22, 2026-10-11): My tasks, on the v2 kit. The page header (eyebrow in the My work
// group, or My stuff in Personal), "+ Add task", and the five views as tabs. Each view draws its own
// section bar inside the tab (embedded). The remembered tab is as it was.
import React, { useState, useEffect } from 'react';
import TaskList from './TaskList';
import WeekView from './WeekView';
import CalendarView from './CalendarView';
import { PageHeader, PageLayout, TabGroup, TabPanel, AddTaskButton } from '../ui';
import { useTasksEyebrow } from './TasksShell';
import OverduePopup from './task/OverduePopup';
import { shownToday, markShownToday } from '../utils/taskDueChanges';
import './tasks.css';

// Today on the person's own clock (their stored timezone), the day the pop-up's once-a-day mark keeps.
function todayIn(tz) {
  const zone = !tz || tz === 'UTC' ? 'Europe/London' : tz;
  try { return new Intl.DateTimeFormat('sv', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); } catch (_) { return null; }
}

const TABS = [
  { id: 'today',    label: 'Today',     color: 'pastel-yellow' },
  { id: 'tomorrow', label: 'Tomorrow',  color: 'pastel-peach' },
  { id: 'week',     label: 'This week', color: 'pastel-blue' },
  { id: 'all',      label: 'All tasks', color: 'pastel-lilac' },
  { id: 'calendar', label: 'Calendar',  color: 'pastel-mint' },
];

const LS_KEY = 'aidey_tasks_active_tab';
const readTab = () => { try { return localStorage.getItem(LS_KEY); } catch (_) { return null; } };
const saveTab = (id) => { try { localStorage.setItem(LS_KEY, id); } catch (_) { /* a private window: not remembered */ } };

function TasksPage({
  displayTasks,
  // THE FLAG App.js ALREADY HAD. It went to the Dashboard only, so every task list below
  // rendered "No tasks match this filter" under a green tick when the fetch had failed.
  tasksLoadFailed = false,
  today,
  tomorrow,
  sortBy,
  setSortBy,
  onViewTask,
  taskTypes,
  undoComplete,
  onMoveTask,
  onAddTask,
  currentUser,
  setSelectedWeekDate,
  setShowWeekModal,
  calendarDate,
  setCalendarDate,
  setSelectedDate,
  setShowCalendarModal,
  tileContent,
  initialTab,
  // Fresh-eyes 15: { counts: { dueToday, overdue } | null, error } from App's useTaskCounts
  // (GET /api/tasks/counts), the same numbers the sidebar badge adds up.
  taskCounts = null,
}) {
  const eyebrow = useTasksEyebrow();
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab) return initialTab;
    const saved = readTab();
    return TABS.some(t => t.id === saved) ? saved : 'today';
  });

  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
      saveTab(initialTab);
    }
  }, [initialTab]); // eslint-disable-line

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    saveTab(tabId);
  };

  // The subline (fresh-eyes 15, Carla, 2026-10-12): the server's two numbers, which never overlap
  // ("3 due today · 17 overdue"). It said "20 due by today · 17 overdue", the 17 inside the 20, and
  // counted them here from a list with every repeating task drawn for a year.
  const counts = taskCounts?.counts || null;
  // Carla, 2026-10-21: the overdue pop-up. It opens by itself once per person per day when anything
  // is overdue (the mark is this browser's convenience; the server decides what is overdue), and any
  // time from "Overdue (N)" in the subline.
  const [overdueOpen, setOverdueOpen] = useState(false);
  const overdueCount = counts?.overdue || 0;
  useEffect(() => {
    if (!overdueCount || !currentUser?.id || tasksLoadFailed) return;
    const day = todayIn(currentUser.timezone);
    if (!day || shownToday(currentUser.id, day)) return;
    markShownToday(currentUser.id, day);
    setOverdueOpen(true);
  }, [overdueCount, currentUser?.id, currentUser?.timezone, tasksLoadFailed]);
  const subline = tasksLoadFailed
    ? 'Your tasks could not be loaded.'
    : counts
      ? (
        <>
          {`${counts.dueToday} due today`}
          {counts.overdue ? <>{' · '}<button type="button" className="ov-link" data-testid="overdue-link" onClick={() => setOverdueOpen(true)}>{`Overdue (${counts.overdue})`}</button></> : null}
        </>
      )
      : taskCounts?.error ? 'Your task counts could not be loaded.' : ' ';

  const listProps = { sortBy, setSortBy, onViewTask, today, tomorrow, taskTypes, undoComplete, onMoveTask, onAddTask, currentUser, embedded: true };

  return (
    <PageLayout data-page="tasks">
      <PageHeader eyebrow={eyebrow} title="My tasks" subline={subline}
        primary={<AddTaskButton onClick={onAddTask} />} />
      <TabGroup label="Task views" idPrefix="tasks" tabs={TABS} value={activeTab} onChange={switchTab} />
      <OverduePopup open={overdueOpen} onClose={() => setOverdueOpen(false)} />

      <TabPanel idPrefix="tasks" tabId={activeTab}>
        {activeTab === 'today' && (
          <TaskList
            tasksLoadFailed={tasksLoadFailed}
            tasks={displayTasks}
            mode="Today"
            {...listProps}
          />
        )}

        {activeTab === 'tomorrow' && (
          <TaskList
            tasksLoadFailed={tasksLoadFailed}
            tasks={displayTasks}
            mode="Tomorrow"
            {...listProps}
          />
        )}

        {activeTab === 'week' && (
          <WeekView
            tasksLoadFailed={tasksLoadFailed}
            tasks={displayTasks}
            today={today}
            embedded
            setSelectedWeekDate={setSelectedWeekDate}
            setShowWeekModal={setShowWeekModal}
            setViewTask={onViewTask}
            onNavigateDay={(date) => {
              const todayStr    = today.toDateString();
              const tomorrowStr = tomorrow.toDateString();
              if (date.toDateString() === todayStr) {
                switchTab('today');
              } else if (date.toDateString() === tomorrowStr) {
                switchTab('tomorrow');
              } else {
                setSelectedWeekDate(date);
                setShowWeekModal(true);
              }
            }}
            onAddTask={onAddTask}
          />
        )}

        {activeTab === 'all' && (
          <TaskList
            tasksLoadFailed={tasksLoadFailed}
            tasks={displayTasks}
            mode="List"
            {...listProps}
          />
        )}

        {activeTab === 'calendar' && (
          <CalendarView
            calendarDate={calendarDate}
            setCalendarDate={setCalendarDate}
            setSelectedDate={setSelectedDate}
            setShowCalendarModal={setShowCalendarModal}
            tileContent={tileContent}
            tasks={displayTasks}
          />
        )}
      </TabPanel>
    </PageLayout>
  );
}

export default TasksPage;
